/**
 * Sessions management page.
 * Tabbed list with {@link SlidePanel} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { CalendarSyncModal } from '@/components/calendar/CalendarSyncModal';
import { CALENDAR_SYNC_ENABLED } from '@/config/features';
import { Button } from '@/components/forms/Button';
import { SlidePanel } from '@/components/common/SlidePanel';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { SelectSessionTypeModal } from '@/components/sessions/remote/SelectSessionTypeModal';
import type { SessionTab } from '@/types/remote/session.types';
import {
  DEFAULT_SESSION_REMOTE_FILTERS,
  SESSION_REMOTE_TAB_COLORS as TAB_COLORS,
  SESSION_REMOTE_TYPE_CONFIG as SESSION_TYPE_CONFIG,
  type SessionRemoteFilters,
} from '@/components/sessions/session-configs';
import { SESSION_TAB_LABELS } from '@/constants/shared-catalog';
import { getSessionTypesForTab, toSessionTab } from '@/utils/common-utils';
import type { TabItem } from '@/components/ui/tabs';
import { LayoutGrid, RefreshCw, X } from 'lucide-react';
import { CreateSessionView } from '@/components/sessions/remote/CreateSessionView';
import { EditSessionView } from '@/components/sessions/remote/EditSessionView';
import { SessionsDataTable } from '@/components/sessions/remote/SessionsDataTable';

/** Header export + optional create button. */
function renderRemoteSessionPageActions(
  canCreate: boolean,
  onCreate: () => void,
  onOpenSync: () => void,
): React.ReactNode {
  return (
    <div className="flex items-center gap-3">
      {CALENDAR_SYNC_ENABLED ? (
        <Button preset="export" icon={RefreshCw} onClick={onOpenSync}>
          Synchroniser
        </Button>
      ) : null}
      {canCreate ? (
        <Button preset="create-entity" onClick={onCreate}>
          Créer une séance
        </Button>
      ) : null}
    </div>
  );
}

/** Remote sessions list with type picker and {@link SlidePanel} create/edit. */
export function SessionsManagementPage() {
  const {
    canCreate,
    canEditRow,
    canCancelRow,
    canExportCsv,
    canViewReportTutoring,
    canViewReportInformationMeeting,
    canViewReportWelcome,
    canViewReportFieldEvent,
    canJoinWelcomeMeeting,
    canJoinInformationMeeting,
    creatableTypes: creatableTypesRaw,
  } = usePageActions('sessions');
  const { elements, isLoading: elementsLoading } = useInterfaceElements('sessions');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [typeModalOpen, setTypeModalOpen] = React.useState(false);
  const [selectedSessionType, setSelectedSessionType] = React.useState<SessionTab | null>(null);
  const [editSessionId, setEditSessionId] = React.useState<string | null>(null);
  const [syncOpen, setSyncOpen] = React.useState(false);

  function handleOpenSync(): void {
    setSyncOpen(true);
  }

  function handleCloseSync(): void {
    setSyncOpen(false);
  }
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const { viewMode, setViewMode } = useViewMode('sessions-remote-management');
  const contentRef = React.useRef<HTMLDivElement>(null);

  /** RBAC-allowed tabs; `undefined` while elements load so the table does not query with the wrong types. */
  const allowedTabIds = React.useMemo((): SessionTab[] | undefined => {
    if (elementsLoading) return undefined;
    const tabElements = elements.filter((el) => el.elementType === 'tab');
    const TAB_ORDER: SessionTab[] = [
      'tutoring',
      'information-meeting',
      'welcome-meeting',
      'field-event',
    ];
    const otherTabs: SessionTab[] =
      tabElements.length === 0
        ? ['tutoring', 'information-meeting', 'welcome-meeting', 'field-event']
        : tabElements
            .map((el) => toSessionTab(el.elementId))
            .filter((id): id is SessionTab => id !== null && id !== 'all');

    const sorted = [...otherTabs].sort((a, b) => TAB_ORDER.indexOf(a) - TAB_ORDER.indexOf(b));
    return sorted;
  }, [elements, elementsLoading]);

  /** Restore the last tab on browser back (`POP`); otherwise default to tutoring. */
  const navigationType = useNavigationType();
  const [activeTab, setActiveTab] = React.useState<SessionTab>(() => {
    if (navigationType === 'POP') {
      const saved = sessionStorage.getItem('dt-tab:sessions') as SessionTab | null;
      if (saved) return saved;
    }
    return 'tutoring';
  });
  const [tableFilters, setTableFilters] = React.useState<SessionRemoteFilters>(
    DEFAULT_SESSION_REMOTE_FILTERS,
  );
  React.useEffect(() => {
    sessionStorage.setItem('dt-tab:sessions', activeTab);
  }, [activeTab]);

  /** Deep-link: filter the list by tutor / student passed via location.state. */
  const [personFilter, setPersonFilter] = React.useState<{
    tutorUserId?: string;
    studentUserId?: string;
    personName?: string;
  }>(() => {
    const state = location.state as {
      tutorUserId?: string;
      studentUserId?: string;
      personName?: string;
    } | null;
    return {
      tutorUserId: state?.tutorUserId,
      studentUserId: state?.studentUserId,
      personName: state?.personName,
    };
  });

  React.useEffect(() => {
    const state = location.state as {
      tutorUserId?: string;
      studentUserId?: string;
      personName?: string;
    } | null;
    if (state?.tutorUserId || state?.studentUserId) {
      setPersonFilter({
        tutorUserId: state.tutorUserId,
        studentUserId: state.studentUserId,
        personName: state.personName,
      });
      void navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  /** Deep-link: open create/edit/view via `?create=` / `?edit=` / `?view=`. */
  React.useEffect(() => {
    const editId = searchParams.get('edit');
    const viewId = searchParams.get('view');
    const shouldCreate = searchParams.get('create') === 'true';

    if (editId) {
      setEditSessionId(editId);
      setSelectedSessionType(null);
      closeRow();
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }

    if (viewId) {
      setOpenRow(viewId);
      setSelectedSessionType(null);
      setEditSessionId(null);
    }

    if (shouldCreate) {
      handleCreateClick();
      setEditSessionId(null);
    }
  }, [searchParams, setSearchParams, closeRow, setOpenRow]);

  /** After the type modal: open the create panel for that session type. */
  const handleTypeSelect = (type: SessionTab) => {
    setSelectedSessionType(type);
    setTypeModalOpen(false);
    closeRow();
  };

  /** Types creatable from `action-create-*` RBAC; falls back to allowed tabs. */
  const creatableTabIds = React.useMemo((): SessionTab[] => {
    const TAB_IDS = new Set<SessionTab>([
      'tutoring',
      'information-meeting',
      'welcome-meeting',
      'field-event',
    ]);
    if (creatableTypesRaw.length > 0) {
      return creatableTypesRaw.filter((id): id is SessionTab => TAB_IDS.has(id as SessionTab));
    }
    return (allowedTabIds ?? []).filter((id) => id !== 'all') as SessionTab[];
  }, [creatableTypesRaw, allowedTabIds]);

  /** Skip the type modal when only one session type is creatable. */
  const handleCreateClick = React.useCallback(() => {
    if (creatableTabIds.length === 1) {
      handleTypeSelect(creatableTabIds[0]);
    } else {
      setTypeModalOpen(true);
      setEditSessionId(null);
      closeRow();
    }
  }, [creatableTabIds, closeRow]);

  /** Open the edit panel and close any expanded details row. */
  const handleEditSession = (sessionId: string) => {
    setEditSessionId(sessionId);
    setSelectedSessionType(null);
    closeRow();
  };

  /** Close create/edit panels and refresh the remote sessions list. */
  const handleFormSuccess = React.useCallback(() => {
    setEditSessionId(null);
    setSelectedSessionType(null);
    void queryClient.invalidateQueries({ queryKey: ['sessions-remote'] });
  }, [queryClient]);

  /** Scroll the page to the form when create or edit first opens. */
  const prevSelectedTypeRef = React.useRef(selectedSessionType);
  const prevEditIdRef = React.useRef(editSessionId);
  React.useEffect(() => {
    const formStateChanged =
      prevSelectedTypeRef.current !== selectedSessionType ||
      prevEditIdRef.current !== editSessionId;
    if (formStateChanged && (selectedSessionType !== null || editSessionId) && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevSelectedTypeRef.current = selectedSessionType;
    prevEditIdRef.current = editSessionId;
  }, [selectedSessionType, editSessionId]);

  /** Drop the pair → sessions pin so the list query is no longer scoped. */
  function handleClearPersonFilter(): void {
    setPersonFilter({
      tutorUserId: undefined,
      studentUserId: undefined,
      personName: undefined,
    });
  }

  /** Banner for a tutor / student / pair deep-link; X matches the pairs page. */
  function renderPersonFilterBanner(): React.ReactNode {
    if (!personFilter.personName) return null;
    const bannerLabel =
      personFilter.tutorUserId && personFilter.studentUserId
        ? `Séances du binôme ${personFilter.personName}`
        : `Séances de ${personFilter.personName}`;
    return (
      <div className="mb-4 p-3 rounded-lg bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] border border-[var(--color-2-nuance-1)] flex items-center justify-between">
        <span className="text-sm font-medium" style={{ color: 'var(--color-2-nuance-1)' }}>
          {bannerLabel}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearPersonFilter}
          tooltipText="Effacer le filtre"
          aria-label="Effacer le filtre"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  const tabItems: TabItem<SessionTab>[] = React.useMemo(
    () =>
      (allowedTabIds ?? []).map((id) => {
        if (id === 'all') {
          return {
            id,
            label: SESSION_TAB_LABELS[id],
            icon: <LayoutGrid className="h-4 w-4" />,
            color: TAB_COLORS[id],
          };
        }
        const typesForTab = getSessionTypesForTab(id);
        const firstType = typesForTab[0]?.type;
        const Icon = firstType ? SESSION_TYPE_CONFIG[firstType]?.icon : undefined;

        return {
          id,
          label: SESSION_TAB_LABELS[id],
          icon: Icon ? <Icon className="h-4 w-4" /> : undefined,
          color: TAB_COLORS[id],
        };
      }),
    [allowedTabIds],
  );

  return (
    <ManagementPage
      title="Gestion des Séances"
      subtitle="Créez, modifiez et gérez toutes vos séances"
      contentRef={contentRef}
      tabs={{
        items: tabItems,
        activeTab,
        onTabChange: (tabId: string) => setActiveTab(tabId as SessionTab),
      }}
      actions={renderRemoteSessionPageActions(canCreate, handleCreateClick, handleOpenSync)}
    >
      {renderPersonFilterBanner()}

      <SessionsDataTable
        activeTab={activeTab}
        allowedTabIds={allowedTabIds}
        expandedRowId={openRowId}
        onCloseDetails={closeRow}
        onEditSession={handleEditSession}
        onView={openRow}
        enableFilters={true}
        onFiltersChange={setTableFilters}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        pageActions={{
          canEditRow,
          canCancelRow,
          canExportCsv,
          canViewReportTutoring,
          canViewReportInformationMeeting,
          canViewReportWelcome,
          canViewReportFieldEvent,
          canJoinWelcomeMeeting,
          canJoinInformationMeeting,
        }}
        pinnedTutorUserId={personFilter.tutorUserId}
        pinnedStudentUserId={personFilter.studentUserId}
      />

      <SelectSessionTypeModal
        open={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        onSelect={handleTypeSelect}
        allowedTabIds={creatableTabIds}
      />

      <SlidePanel
        isOpen={selectedSessionType !== null}
        onClose={() => setSelectedSessionType(null)}
      >
        {selectedSessionType && (
          <CreateSessionView
            sessionType={selectedSessionType}
            onClose={() => setSelectedSessionType(null)}
            onSuccess={handleFormSuccess}
          />
        )}
      </SlidePanel>

      <SlidePanel isOpen={!!editSessionId} onClose={() => setEditSessionId(null)}>
        {editSessionId && (
          <EditSessionView
            key={editSessionId}
            sessionId={editSessionId}
            onClose={() => setEditSessionId(null)}
            onSuccess={handleFormSuccess}
          />
        )}
      </SlidePanel>
      {CALENDAR_SYNC_ENABLED ? (
        <CalendarSyncModal
          open={syncOpen}
          onClose={handleCloseSync}
          lockedTypes={['session']}
          filterSeed={{
            sessionTab: activeTab,
            sessionRemoteFilters: {
              ...tableFilters,
              tutorUserId: personFilter.tutorUserId ?? tableFilters.tutorUserId,
              studentUserId: personFilter.studentUserId ?? tableFilters.studentUserId,
            },
          }}
        />
      ) : null}
    </ManagementPage>
  );
}
